import BottomSheet from '@basic/BottomSheet';
import Button from '@basic/Button';
import Loader from '@basic/Loader';
import { redirectToUrl } from '@components/goal-packages/navigation';
import LandingPageFooter from '@components/shared/LandingPageFooter';
import { PaymentsErrorCode } from '@consts';
import { useOrdersService } from '@hooks/useOrdersService';
import { usePaymentsService } from '@hooks/usePaymentsService';
import ImprvCoinIcon from '@icons/ImprvCoinIcon';
import { ToastType } from '@models/common';
import {
  ImprvCoinAcquisitionKind,
  ImprvCoinLedgerEntryKind,
  ImprvCoinsPackageModel
} from '@models/imprvCoins';
import { PaymentSessionReason, ProductType } from '@models/payments';
import * as Sentry from '@sentry/nextjs';
import { saveAssignmentTaskDrafts } from '@services/assignmentTaskDrafts';
import { saveImprvCoinsPurchaseContext } from '@services/imprvCoinsPurchase';
import { useStore } from '@store';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImprvCoinPackageOption from './ImprvCoinPackageOption';

const REENABLE_COOLDOWN_MS = 2000;

// the cheapest package's coins-per-grosz rate is the baseline for the "+X% more coins" lines
function bonusBaseline(packages: ImprvCoinsPackageModel[]): ImprvCoinsPackageModel | null {
  if (packages.length === 0) return null;
  return packages.reduce((min, pkg) => (pkg.price.amount < min.price.amount ? pkg : min));
}

function bonusPercentVsBase(pkg: ImprvCoinsPackageModel, base: ImprvCoinsPackageModel): number {
  const rate = (p: ImprvCoinsPackageModel) =>
    p.price.amount > 0 ? p.quantity / p.price.amount : 0;
  const baseRate = rate(base);
  if (baseRate === 0) return 0;
  return Math.max(0, Math.round((rate(pkg) / baseRate - 1) * 100));
}

function defaultSku(packages: ImprvCoinsPackageModel[]): string | null {
  return packages.find((pkg) => pkg.isRecommended)?.sku ?? packages[0]?.sku ?? null;
}

export default function BuyImprvCoinsModal() {
  const { t } = useTranslation();
  const router = useRouter();
  const { isOpen } = useStore((s) => s.buyImprvCoinsModalState);
  const closeBuyImprvCoinsModal = useStore((s) => s.closeBuyImprvCoinsModal);
  const setToast = useStore((s) => s.setToast);
  const taskOutput = useStore((s) => s.taskOutput);
  const { getImprvCoinsPackages, createPaymentSession } = usePaymentsService();
  const { getImprvCoinsLedger } = useOrdersService();

  const [packages, setPackages] = useState<ImprvCoinsPackageModel[] | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  // guards against a stale fetch (fast close->reopen) winning the race
  const fetchGenerationRef = useRef(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadPackages = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    setFetchFailed(false);
    try {
      const fetched = await getImprvCoinsPackages();
      if (generation !== fetchGenerationRef.current) return;
      if (fetched.length === 0) {
        Sentry.captureMessage('Empty imprv coins package catalog');
      }
      setPackages(fetched);
      setSelectedSku(defaultSku(fetched));
    } catch {
      if (generation !== fetchGenerationRef.current) return;
      setFetchFailed(true);
    }
  }, [getImprvCoinsPackages]);

  useEffect(() => {
    if (!isOpen) return;
    loadPackages();
  }, [isOpen, loadPackages]);

  useEffect(() => {
    if (isOpen) return;
    fetchGenerationRef.current++;
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
    setIsPending(false);
    setFetchFailed(false);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const selectedPkg = packages?.find((p) => p.sku === selectedSku) ?? null;
  const baselinePkg = packages ? bonusBaseline(packages) : null;
  const priceFormatter = selectedPkg
    ? new Intl.NumberFormat('pl-PL', { style: 'currency', currency: selectedPkg.price.currency })
    : null;

  const handleBuy = async () => {
    if (!selectedPkg || isPending) return;
    setIsPending(true);
    try {
      let knownAcquisitionIds: string[] | null = null;
      try {
        const ledger = await getImprvCoinsLedger();
        knownAcquisitionIds = ledger.entries
          .filter(
            (entry) =>
              entry.kind === ImprvCoinLedgerEntryKind.ACQUISITION &&
              entry.acquisitionKind === ImprvCoinAcquisitionKind.PURCHASE
          )
          .map((entry) => entry.id);
      } catch {
        // best-effort snapshot; the thank-you page falls back to quantity matching
      }
      saveImprvCoinsPurchaseContext({
        origin: router.asPath,
        knownAcquisitionIds,
        sku: selectedPkg.sku
      });
      // Paying leaves the page, which drops every answer typed in the open task.
      saveAssignmentTaskDrafts(router.query.nodeEntityId as string, taskOutput);

      const session = await createPaymentSession({
        product: { type: ProductType.IMPRV_COINS, data: { packageSku: selectedPkg.sku } }
      });
      if (session.reason === PaymentSessionReason.SESSION_CREATED && session.sessionRedirectUrl) {
        redirectToUrl(session.sessionRedirectUrl);
        return; // keep isPending – the page is navigating away
      }
      Sentry.captureMessage('Unexpected imprv coins payment session response', {
        extra: { sku: selectedPkg.sku, session }
      });
      setToast({ message: 'components.buyImprvCoinsModal.error', type: ToastType.ERROR });
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setIsPending(false), REENABLE_COOLDOWN_MS);
    } catch (error) {
      const errorCode = (error as { errorCode?: string })?.errorCode;
      setToast({
        message:
          errorCode === PaymentsErrorCode.UNKNOWN_IMPRV_COINS_PACKAGE
            ? 'components.buyImprvCoinsModal.packageUnavailable'
            : 'components.buyImprvCoinsModal.error',
        type: ToastType.ERROR
      });
      Sentry.captureException(error, {
        extra: { sku: selectedPkg.sku, context: 'buy imprv coins' }
      });
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setIsPending(false), REENABLE_COOLDOWN_MS);
    }
  };

  const ctaLabel = isPending
    ? t('domains.payments.CTAs.processing')
    : t('components.buyImprvCoinsModal.cta', {
        price:
          priceFormatter && selectedPkg ? priceFormatter.format(selectedPkg.price.amount / 100) : ''
      });

  const renderCatalog = () => {
    // an empty catalog is unusable, so it gets the same retry affordance as a failed fetch
    if (fetchFailed || packages?.length === 0) {
      return (
        <div className='flex flex-col items-center gap-md mt-lg'>
          <div className={classNames('P4R text-center', setupForDark('text-secondary'))}>
            {t('components.buyImprvCoinsModal.fetchError')}
          </div>
          <Button
            size='small'
            type='secondary'
            fitContent
            onClick={loadPackages}
            data-testid='buy-imprv-coins-retry'
          >
            {t('components.buyImprvCoinsModal.retry')}
          </Button>
        </div>
      );
    }

    if (!packages) {
      return (
        <div className='flex justify-center mt-lg'>
          <Loader />
        </div>
      );
    }

    return (
      <>
        <div
          role='radiogroup'
          aria-label={t('components.buyImprvCoinsModal.title')}
          className='flex flex-col gap-sm mt-lg'
        >
          {packages.map((pkg) => (
            <ImprvCoinPackageOption
              key={pkg.sku}
              pkg={pkg}
              selected={pkg.sku === selectedSku}
              showBadge={pkg.isRecommended}
              bonusPercent={
                baselinePkg && pkg.sku !== baselinePkg.sku
                  ? bonusPercentVsBase(pkg, baselinePkg)
                  : null
              }
              onSelect={() => setSelectedSku(pkg.sku)}
              disabled={isPending}
            />
          ))}
        </div>
        <div className='mt-lg'>
          <Button
            size='modal'
            type='primary'
            fullWidth
            disabled={isPending || !selectedPkg}
            onClick={handleBuy}
            data-testid='buy-imprv-coins-cta'
          >
            {ctaLabel}
          </Button>
        </div>
        <div
          className={classNames('P6R text-center mt-xs', setupForDark('text-tertiary'))}
          data-testid='buy-imprv-coins-vat-info'
        >
          {t('components.buyImprvCoinsModal.vatInfo')}
        </div>
      </>
    );
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={closeBuyImprvCoinsModal}
      desktopVersion='modal'
      detent='content'
    >
      <div className='dk:w-[440px] mx-auto flex flex-col px-md' data-testid='buy-imprv-coins-modal'>
        <div className='flex flex-row justify-center'>
          <ImprvCoinIcon size={32} />
        </div>
        <div className={classNames('H5 text-center mt-md', setupForDark('text-primary'))}>
          {t('components.buyImprvCoinsModal.title')}
        </div>
        <div className={classNames('P4R text-center mt-xs', setupForDark('text-secondary'))}>
          {t('components.buyImprvCoinsModal.subtitle')}
        </div>

        {renderCatalog()}

        <div className='mt-md mb-md'>
          <LandingPageFooter variant='modal' />
        </div>
      </div>
    </BottomSheet>
  );
}
